<template>
    <button
        type="button"
        :class="buttonClasses"
        :style="inlineStyle"
        :title="title"
        :disabled="disabled"
        :aria-label="title || icon"
        v-bind="$attrs"
        @click="$emit('click', $event)"
    >
        <i v-if="icon" :class="iconClass"></i>
        <slot v-else></slot>
    </button>
</template>

<script>
export default {
    name: "IconButton",
    inheritAttrs: false,
    props: {
        /** FontAwesome 아이콘 클래스 (예: 'fas fa-plus', 'fas fa-edit') */
        icon: {
            type: String,
            default: "",
        },
        /** 버튼 툴팁/접근성 텍스트 */
        title: {
            type: String,
            default: "",
        },
        /** 크기: default(테이블 행용), compact(툴바 32px), small(24px) */
        size: {
            type: String,
            default: "default",
            validator: (v) => ["default", "compact", "small"].includes(v),
        },
        /** 스타일 변형: default, delete(삭제·위험 액션) */
        variant: {
            type: String,
            default: "default",
            validator: (v) => ["default", "delete", "danger"].includes(v),
        },
        /** 아이콘/폰트 기본 색상 (기본값 #94a3b8) */
        color: {
            type: String,
            default: "#94a3b8",
        },
        /** 테두리 있는 스타일 (추가 버튼 등) */
        bordered: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /** 추가 클래스 (문자열 또는 배열) */
        customClass: {
            type: [String, Array],
            default: "",
        },
    },
    computed: {
        iconClass() {
            const base = this.icon.trim();
            return base || "fas fa-circle";
        },
        buttonClasses() {
            const list = ["icon-button"];
            list.push(`icon-button--${this.size}`);
            if (this.variant !== "default") list.push(`icon-button--${this.variant}`);
            if (this.bordered) list.push("icon-button--bordered");
            const custom = this.customClass;
            if (custom) {
                if (Array.isArray(custom)) list.push(...custom.filter(Boolean));
                else list.push(custom);
            }
            return list;
        },
        inlineStyle() {
            return { "--icon-btn-color": this.color };
        },
    },
};
</script>

<style scoped>
.icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--icon-btn-color, #94a3b8);
    border-radius: 4px;
    cursor: pointer;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
    font-size: inherit;
}

.icon-button:focus {
    outline: none;
}

.icon-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* size: default - 테이블 행 액션 */
.icon-button--default {
    padding: 4px 8px;
    min-width: 28px;
    min-height: 26px;
    font-size: 14px;
}

.icon-button--default:hover:not(:disabled) {
    color: #1e293b;
}

.icon-button--default.icon-button--delete:hover:not(:disabled),
.icon-button--default.icon-button--danger:hover:not(:disabled) {
    color: #dc2626;
}

/* size: compact - 툴바(32px) */
.icon-button--compact {
    min-width: 32px;
    min-height: 32px;
    font-size: 13px;
}

.icon-button--compact:hover:not(:disabled) {
    background: #f0f9f4;
    color: #1e293b;
}

.icon-button--compact.icon-button--bordered {
    border: 1px solid #e2e8f0;
    background: #ffffff;
}

.icon-button--compact.icon-button--bordered:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
}

/* size: small - 작은 액션(24px) */
.icon-button--small {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    font-size: 12px;
}

.icon-button--small:hover:not(:disabled) {
    color: #64748b;
}

.icon-button--small.icon-button--delete:hover:not(:disabled),
.icon-button--small.icon-button--danger:hover:not(:disabled) {
    color: #dc2626;
}

/* bordered - 헤더/추가 버튼 스타일 */
.icon-button--bordered:not(.icon-button--compact) {
    width: 28px;
    height: 28px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 6px;
}

.icon-button--bordered:not(.icon-button--compact):hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
}

.icon-button i {
    font-size: inherit;
}
</style>
